import { useEffect, useMemo, useRef, useState } from "react";
import type {
  ImageWorkerRequest,
  ImageWorkerResponse,
  WorldEnvironment,
  WorldMessage,
} from "./types";
import {
  actualMessages,
  defaultEnvironment,
  imagePrompt,
  parseActualModels,
  parseActualText,
} from "./world";
import { AppSwitcherDialog } from "./AppSwitcherDialog";
import { WorldOrb } from "./WorldOrb";

const keyStorage = "centience-worlds-actual-key";
const modelStorage = "centience-worlds-actual-model";
const clusterStorage = "centience-worlds-actual-cluster";
let nextIdentifier = 0;

function identifier(): string {
  nextIdentifier += 1;
  return `world-${Date.now().toString(36)}-${nextIdentifier.toString(36)}`;
}

function welcomeMessage(): WorldMessage {
  return {
    id: identifier(),
    role: "assistant",
    content: "CENTIENCE WORLDS 1.0\n\nActual Computer link idle. Configure a key, then describe somewhere that should exist.",
    createdAt: Date.now(),
  };
}

function percent(value: number): string {
  return `${Math.round(Math.max(0, Math.min(1, value)) * 100)}%`;
}

interface SettingsProps {
  open: boolean;
  actualKey: string;
  model: string;
  cluster: string;
  onClose: () => void;
  onForget: () => void;
  onSave: (settings: { actualKey: string; model: string; cluster: string }) => void;
}

function SettingsDialog({ open, actualKey, model, cluster, onClose, onForget, onSave }: SettingsProps) {
  const [draftKey, setDraftKey] = useState(actualKey);
  const [draftModel, setDraftModel] = useState(model);
  const [draftCluster, setDraftCluster] = useState(cluster);
  const [models, setModels] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDraftKey(actualKey);
    setDraftModel(model);
    setDraftCluster(cluster);
    setError(null);
  }, [actualKey, cluster, model, open]);

  if (!open) return null;
  const discoverModels = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch("/api/actual/models", {
        headers: {
          "x-actual-api-key": draftKey.trim(),
          ...(draftCluster.trim() ? { "x-actual-cluster-id": draftCluster.trim() } : {}),
        },
      });
      const body = await response.json() as unknown;
      if (!response.ok) {
        const message = body && typeof body === "object" && typeof (body as { error?: unknown }).error === "string"
          ? (body as { error: string }).error
          : `Actual returned ${response.status}`;
        throw new Error(message);
      }
      const discovered = parseActualModels(body);
      setModels(discovered);
      if (!draftModel && discovered[0]) setDraftModel(discovered[0]);
      if (!discovered.length) setError("The connected Actual cluster did not advertise a loaded model.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to reach Actual Computer");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && actualKey) onClose(); }}>
      <section className="settings-dialog" role="dialog" aria-modal="true" aria-labelledby="settings-title">
        <header><div><span>Private compute</span><h2 id="settings-title">Actual Computer</h2></div>{actualKey ? <button type="button" onClick={onClose} aria-label="Close settings">×</button> : null}</header>
        <div className="settings-copy">
          <p>Bring an Actual inference key. It stays in this tab’s session storage and is forwarded without retention only when you send a message.</p>
          <a href="https://actual.inc/user/keys" target="_blank" rel="noreferrer">Create or manage Actual keys ↗</a>
        </div>
        <div className="settings-fields">
          <label><span>Inference key</span><input type="password" autoComplete="off" value={draftKey} onChange={(event) => setDraftKey(event.target.value)} placeholder="ac_…" /></label>
          <label><span>Cluster ID <em>optional</em></span><input value={draftCluster} onChange={(event) => setDraftCluster(event.target.value)} /></label>
          <label><span>Chat model</span><input list="actual-models" value={draftModel} onChange={(event) => setDraftModel(event.target.value)} placeholder="Discover or enter a loaded model" /></label>
          <datalist id="actual-models">{models.map((name) => <option key={name} value={name} />)}</datalist>
          <button className="secondary-button" type="button" disabled={loading || !draftKey.trim()} onClick={() => void discoverModels()}>{loading ? "Checking…" : "Discover models"}</button>
          {error ? <p className="settings-error" role="alert">{error}</p> : null}
        </div>
        <footer><button className="secondary-button" type="button" onClick={() => { setDraftKey(""); onForget(); }}>Forget key</button><button className="primary-button" type="button" disabled={!draftKey.trim()} onClick={() => onSave({ actualKey: draftKey.trim(), model: draftModel.trim(), cluster: draftCluster.trim() })}>Use this computer</button></footer>
      </section>
    </div>
  );
}

export function App() {
  const [messages, setMessages] = useState<WorldMessage[]>([welcomeMessage()]);
  const [input, setInput] = useState("");
  const [environment, setEnvironment] = useState<WorldEnvironment>(defaultEnvironment);
  const [actualKey, setActualKey] = useState(() => sessionStorage.getItem(keyStorage) ?? "");
  const [model, setModel] = useState(() => sessionStorage.getItem(modelStorage) ?? "");
  const [cluster, setCluster] = useState(() => sessionStorage.getItem(clusterStorage) ?? "");
  const [settingsOpen, setSettingsOpen] = useState(() => !sessionStorage.getItem(keyStorage));
  const [appSwitcherOpen, setAppSwitcherOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageSupported, setImageSupported] = useState<boolean | null>(null);
  const [imageState, setImageState] = useState("Checking local graphics");
  const [imageProgress, setImageProgress] = useState(0);
  const [imageBusy, setImageBusy] = useState(false);
  const [autoImages, setAutoImages] = useState(true);
  const workerRef = useRef<Worker | null>(null);
  const pendingImageMessage = useRef<string | null>(null);
  const terminalRef = useRef<HTMLDivElement>(null);

  const latestImage = useMemo(
    () => [...messages].reverse().find((message) => message.imageUrl)?.imageUrl ?? null,
    [messages],
  );

  useEffect(() => {
    const worker = new Worker(new URL("./image.worker.ts", import.meta.url), { type: "module" });
    workerRef.current = worker;
    const receive = (event: MessageEvent<ImageWorkerResponse>) => {
      const message = event.data;
      if (message.type === "capability") {
        setImageSupported(message.supported);
        setImageState(message.supported ? `WebGPU ready${message.fp16 ? " · FP16" : ""}` : message.message ?? "WebGPU unavailable");
      } else if (message.type === "loading") {
        setImageBusy(true);
        setImageState(message.message);
      } else if (message.type === "download") {
        setImageBusy(true);
        setImageProgress(message.progress);
        setImageState(`Loading ${message.file.split("/").at(-1) ?? "model"}`);
      } else if (message.type === "ready") {
        setImageState("Local model ready");
      } else if (message.type === "generating") {
        setImageBusy(true);
        setImageProgress(message.progress);
        setImageState(`Synthesizing locally · ${Math.round(message.elapsedMs / 1000)}s`);
      } else if (message.type === "result") {
        const target = pendingImageMessage.current;
        const url = URL.createObjectURL(message.blob);
        setMessages((current) => current.map((entry) => entry.id === target ? { ...entry, imageUrl: url } : entry));
        setImageBusy(false);
        setImageProgress(1);
        setImageState("Local image complete");
        pendingImageMessage.current = null;
      } else if (message.type === "error") {
        setImageBusy(false);
        setImageState("Local image unavailable");
        setError(message.message);
        pendingImageMessage.current = null;
      }
    };
    worker.addEventListener("message", receive);
    const request: ImageWorkerRequest = { type: "check" };
    worker.postMessage(request);
    return () => {
      worker.removeEventListener("message", receive);
      worker.terminate();
      workerRef.current = null;
    };
  }, []);

  useEffect(() => {
    terminalRef.current?.scrollTo({ top: terminalRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const saveSettings = (next: { actualKey: string; model: string; cluster: string }) => {
    setActualKey(next.actualKey);
    setModel(next.model);
    setCluster(next.cluster);
    sessionStorage.setItem(keyStorage, next.actualKey);
    if (next.model) sessionStorage.setItem(modelStorage, next.model);
    else sessionStorage.removeItem(modelStorage);
    if (next.cluster) sessionStorage.setItem(clusterStorage, next.cluster);
    else sessionStorage.removeItem(clusterStorage);
    setSettingsOpen(false);
  };

  const forgetSettings = () => {
    setActualKey("");
    setModel("");
    setCluster("");
    sessionStorage.removeItem(keyStorage);
    sessionStorage.removeItem(modelStorage);
    sessionStorage.removeItem(clusterStorage);
  };

  const generateLocalImage = (targetId: string, prompt: string) => {
    if (!imageSupported || imageBusy || !workerRef.current) return;
    pendingImageMessage.current = targetId;
    setImageBusy(true);
    setImageProgress(0);
    const request: ImageWorkerRequest = { type: "generate", prompt };
    workerRef.current.postMessage(request);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const content = input.trim();
    if (!content || sending) return;
    if (!actualKey) {
      setSettingsOpen(true);
      return;
    }
    const userMessage: WorldMessage = { id: identifier(), role: "user", content, createdAt: Date.now() };
    const conversation = [...messages, userMessage];
    setMessages(conversation);
    setInput("");
    setSending(true);
    setError(null);
    try {
      const response = await fetch("/api/actual/chat", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-actual-api-key": actualKey,
          ...(cluster ? { "x-actual-cluster-id": cluster } : {}),
        },
        body: JSON.stringify({
          ...(model ? { model } : {}),
          messages: actualMessages(conversation),
          temperature: 0.82,
          max_tokens: 700,
          stream: false,
        }),
      });
      const body = await response.json() as unknown;
      if (!response.ok) {
        const message = body && typeof body === "object" && typeof (body as { error?: unknown }).error === "string"
          ? (body as { error: string }).error
          : `Actual returned ${response.status}`;
        throw new Error(message);
      }
      const text = parseActualText(body);
      if (!text) throw new Error("Actual returned an empty response");
      const assistant: WorldMessage = { id: identifier(), role: "assistant", content: text, createdAt: Date.now() };
      const completed = [...conversation, assistant];
      setMessages(completed);
      if (autoImages && imageSupported && !imageBusy) {
        const prompt = imagePrompt(completed, environment);
        setMessages((current) => current.map((entry) => entry.id === assistant.id ? { ...entry, imagePrompt: prompt } : entry));
        generateLocalImage(assistant.id, prompt);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to reach Actual Computer");
    } finally {
      setSending(false);
    }
  };

  const clearSession = () => {
    messages.forEach((message) => { if (message.imageUrl) URL.revokeObjectURL(message.imageUrl); });
    setMessages([welcomeMessage()]);
    setError(null);
  };

  return (
    <main className="worlds-shell">
      <header className="worlds-header">
        <div className="worlds-brand">
          <b>CENTIENCE</b>
          <button
            className="worlds-app-switcher-trigger"
            type="button"
            aria-label="Switch Centience app. Current app: Worlds"
            aria-haspopup="dialog"
            aria-expanded={appSwitcherOpen}
            onClick={() => setAppSwitcherOpen(true)}
          >
            <span>Worlds</span><span className="worlds-app-switcher-chevron" aria-hidden="true">⌄</span>
          </button>
        </div>
        <div className="worlds-status"><i className={actualKey ? "online" : ""} /><span>{actualKey ? "Actual linked" : "Actual offline"}</span><button type="button" onClick={() => setSettingsOpen(true)}>Compute</button></div>
      </header>

      <section className="worlds-stage">
        <section className="terminal-panel">
          <header><span>world_sim.exe</span><div><button type="button" onClick={clearSession}>Clear</button><button type="button" onClick={() => setSettingsOpen(true)}>Settings</button></div></header>
          <div className="terminal-output" ref={terminalRef}>
            {messages.map((message) => (
              <article key={message.id} className={`message ${message.role}`}>
                <span>{message.role === "user" ? "simulator@worlds:~$" : "worlds>"}</span>
                <p>{message.content}</p>
                {message.imageUrl ? <button className="inline-image" type="button" onClick={() => window.open(message.imageUrl, "_blank", "noopener,noreferrer")}><img src={message.imageUrl} alt="Locally generated view of this world" /></button> : null}
              </article>
            ))}
            {sending ? <div className="terminal-wait"><i /><span>Actual Computer is thinking</span></div> : null}
          </div>
          <form className="terminal-input" onSubmit={(event) => void submit(event)}>
            <span>›</span><textarea value={input} onChange={(event) => setInput(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); event.currentTarget.form?.requestSubmit(); } }} placeholder="Describe a place, action, or impossible condition…" rows={1} /><button type="submit" disabled={sending || !input.trim()}>Enter</button>
          </form>
          {error ? <p className="world-error" role="alert">{error}</p> : null}
        </section>

        <section className="world-view">
          <header><span>World renderer</span><small>{latestImage ? "LOCAL FRAME" : "PROCEDURAL IDLE"}</small></header>
          <div className="world-canvas">
            {latestImage ? <img src={latestImage} alt="Latest locally generated world" /> : <WorldOrb environment={environment} />}
            <div className="world-coordinate">{environment.light.toFixed(1)} / {environment.void.toFixed(1)} / {messages.length.toString().padStart(3, "0")}</div>
          </div>
          <div className="image-runtime">
            <div><b>{imageState}</b><span>{imageSupported === false ? "Chat remains available" : "Janus Pro 1B · private WebGPU/WASM"}</span></div>
            <div className="runtime-progress"><i style={{ width: percent(imageProgress) }} /></div>
            <label><input type="checkbox" checked={autoImages} disabled={imageSupported === false} onChange={(event) => setAutoImages(event.target.checked)} />Generate local frames</label>
            {imageSupported && !imageBusy ? <button type="button" onClick={() => workerRef.current?.postMessage({ type: "load" } satisfies ImageWorkerRequest)}>Load model</button> : null}
          </div>
        </section>
      </section>

      <section className="world-controls">
        <header><span>Environment</span><small>Parameters condition the next local frame</small></header>
        <div className="environment-grid">
          {(Object.keys(environment) as Array<keyof WorldEnvironment>).map((name) => (
            <label key={name}><span>{name}<b>{environment[name]}</b></span><input type="range" min="0" max="10" step="1" value={environment[name]} onChange={(event) => setEnvironment((current) => ({ ...current, [name]: Number(event.target.value) }))} /></label>
          ))}
        </div>
        <div className="world-filmstrip">
          {messages.filter((message) => message.imageUrl).map((message, index) => <button type="button" key={message.id} onClick={() => window.open(message.imageUrl, "_blank", "noopener,noreferrer")}><img src={message.imageUrl} alt={`Generated world ${index + 1}`} /><span>{String(index + 1).padStart(2, "0")}</span></button>)}
          {!messages.some((message) => message.imageUrl) ? <p>Local frames will collect here without being uploaded.</p> : null}
        </div>
      </section>

      <SettingsDialog open={settingsOpen} actualKey={actualKey} model={model} cluster={cluster} onClose={() => setSettingsOpen(false)} onForget={forgetSettings} onSave={saveSettings} />
      {appSwitcherOpen ? <AppSwitcherDialog location={window.location} onClose={() => setAppSwitcherOpen(false)} /> : null}
    </main>
  );
}
